import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import MultiSelect from '../ui/multi-select';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import LoadingOverlay, { TableLoadingOverlay, InlineLoader } from '../ui/loading-overlay';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Search, 
  Filter, 
  Download, 
  Eye,
  MoreHorizontal,
  RefreshCw,
  X,
  Copy,
  CheckCircle,
  AlertCircle,
  Clock,
  CreditCard,
  Settings,
  RotateCcw,
  Calendar,
  DollarSign,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Trash2,
  Globe,
  Smartphone,
  Monitor,
  ShoppingBag,
  Phone,
  Code,
  ChevronDown,
  FileText,
  FileSpreadsheet,
  FileJson,
  FileType,
  Columns,
  Layers,
  LayoutList,
  LayoutGrid
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem
} from '../ui/dropdown-menu';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePlatform } from '../../contexts/PlatformContext';
import { useReference } from '../../contexts/ReferenceContext';
import { useToast } from '../../hooks/use-toast';
import { getCountryName, getCountryFlag, getCountryAlpha2 } from '../../utils/countries';
import { getChannelIcon } from '../../utils/channelIcons';
import MerchantSearchSelect from '../ui/merchant-search-select';
import { notifySidebarCount } from '../../lib/sidebar-counts';
import { 
  formatCurrencyForOtherScreens,
  formatNumber as formatNumberForOtherScreens,
  formatCurrencySelector,
  getCurrencyDisplay 
} from '../../utils/currencyFormatter';

// Helper function for currency formatting (uses centralized formatter with 2 decimals)
const formatCurrency = (amount, currency = 'USD') => {
  return formatCurrencyForOtherScreens(amount, currency);
};

// Helper for formatting large numbers (uses centralized formatter)
const formatNumber = (number) => {
  return formatNumberForOtherScreens(number);
};

// MetricCard Component for Transaction Totals
const MetricCard = ({ title, value, icon: Icon, subtitle, className }) => (
  <Card className={`relative overflow-hidden group hover:shadow-lg transition-all duration-300 ${className || ''}`}>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      <Icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold text-foreground">{value}</div>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </CardContent>
  </Card>
);



// Default column configuration
const DEFAULT_COLUMNS = [
  { id: 'actions', label: 'Actions', width: 'w-12', fixed: true },
  { id: 'date', label: 'Date', width: 'auto' },
  { id: 'transaction_type', label: 'Type / Channel', width: 'auto' },
  { id: 'reference', label: 'Reference/Desc', width: 'min-w-[20ch]' },
  { id: 'amount', label: 'Amount', width: 'auto' },
  { id: 'status', label: 'Status/Result', width: 'min-w-[20ch] w-64' },
  { id: 'customer', label: 'Customer', width: 'auto' },
  { id: 'payment_method', label: 'Payment Method', width: 'auto' },
  { id: 'card', label: 'Card', width: 'auto' },
  { id: 'approval_code', label: 'Approval', width: 'auto' },
  { id: 'ip_info', label: 'IP Info', width: 'min-w-[140px]' },
  { id: 'risk_action', label: 'Risk Action', width: 'auto' },
  { id: 'platform_processor', label: 'Platform/Processor', width: 'auto' },
  { id: 'merchant', label: 'Merchant', width: 'min-w-[25ch]' },
  { id: 'transaction_id', label: 'Transaction ID', width: 'min-w-[140px]' }
];

// Map a grid column id -> list of export fields (in the order they should appear in the export)
// Used to translate the user's drag-ordered grid columns into export field order.
const GRID_COLUMN_TO_EXPORT_FIELDS = {
  actions: [], // UI only
  date: ['transaction_date'],
  transaction_type: ['transaction_type', 'channel'],
  reference: ['reference', 'description'],
  amount: ['amount', 'currency', 'converted_amount', 'converted_currency'],
  status: ['status', 'result_code'],
  customer: ['customer_name', 'customer_email', 'customer_country'],
  payment_method: ['payment_method'],
  card: ['card_masked', 'card_brand', 'card_type', 'card_bank', 'card_country'],
  approval_code: ['approval_code'],
  ip_info: ['ip', 'ip_country'],
  risk_action: ['risk_action', 'risk_score'],
  platform_processor: ['platform_id', 'payment_processor'],
  merchant: ['merchant_name'],
  transaction_id: ['id', 'transaction_id', 'bank_reference', 'country']
};

// Build an ordered list of export field names from a grid column order array.
const buildExportFieldsFromColumnOrder = (columnOrderIds) => {
  const fields = [];
  const seen = new Set();
  for (const id of columnOrderIds || []) {
    const mapped = GRID_COLUMN_TO_EXPORT_FIELDS[id] || [];
    for (const f of mapped) {
      if (!seen.has(f)) {
        seen.add(f);
        fields.push(f);
      }
    }
  }
  return fields;
};

// Draggable Header Component
const DraggableHeader = ({ column, isFixed }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: column.id,
    disabled: isFixed
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <th
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`
        ${column.width} 
        ${isFixed ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'}
        ${isDragging ? 'z-50' : ''}
        bg-gray-100 px-4 py-3 ${column.id === 'amount' ? 'text-right' : column.id === 'payment_method' ? 'text-center' : 'text-left'} text-sm font-medium text-gray-600
        ${isFixed ? 'bg-gray-100' : 'hover:bg-gray-200'}
        whitespace-nowrap
      `}
    >
      <div className={`flex items-center gap-2 ${column.id === 'amount' ? 'justify-end' : column.id === 'payment_method' ? 'justify-center' : ''}`}>
        {column.label}
      </div>
    </th>
  );
};

const StatusBadge = ({ status }) => {
  // Show checkmark with "Completed" text for completed status with green background
  if (status === 'completed') {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-200 capitalize">
        <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
        Completed
      </Badge>
    );
  }
  
  // Show "Pending" with gray background
  if (status === 'pending') {
    return (
      <Badge className="bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200 capitalize">
        Pending
      </Badge>
    );
  }
  
  // All other statuses show with light red background
  return (
    <Badge className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100 capitalize">
      {status}
    </Badge>
  );
};

const ResultCodeBadge = ({ resultCode, resultCodes = [] }) => {
  const isApproved = resultCode?.includes('Approved') || resultCode?.includes('approved') || 
                    resultCode === '00' || resultCode === '10' || resultCode === '11';
  
  const resultCodeInfo = resultCodes.find(rc => rc.code === resultCode);
  const fullName = resultCodeInfo?.name || resultCodeInfo?.description || '';
  
  // Extract description part (strip code prefix if name starts with code)
  const cleanDesc = fullName.startsWith(resultCode) ? fullName.slice(resultCode.length).replace(/^[\s-]+/, '') : fullName;
  
  // Check for 3DS Declined or Risk Score Declined
  const lowerDesc = cleanDesc?.toLowerCase() || '';
  const is3DSDeclined = lowerDesc.includes('3ds') || lowerDesc.includes('3d secure') || lowerDesc.includes('3d_secure');
  const isRiskDeclined = lowerDesc.includes('risk');
  const isOrangeStatus = is3DSDeclined || isRiskDeclined;
  
  // Always show code: description
  const displayText = cleanDesc && cleanDesc !== resultCode
    ? `${resultCode}: ${cleanDesc}`
    : resultCode;
  
  if (isApproved) {
    return (
      <Badge className="text-xs font-medium bg-green-100 text-green-800 border-green-200 hover:bg-green-200">
        {displayText}
      </Badge>
    );
  } else if (isOrangeStatus) {
    return (
      <Badge 
        className="text-xs font-medium border hover:opacity-80"
        style={{ backgroundColor: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' }}
      >
        {displayText}
      </Badge>
    );
  } else {
    return (
      <Badge className="text-xs font-medium bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200">
        {displayText}
      </Badge>
    );
  }
};

const PaymentMethodIcon = ({ method, brand, paymentMethodLogos = {} }) => {
  const getCardBrandIcon = (cardBrand) => {
    const brandLower = cardBrand?.toLowerCase();
    
    switch (brandLower) {
      case 'visa':
        return (
          <img 
            src="/images/payment-methods/visa.png" 
            alt="Visa"
            className="h-5 w-auto object-contain"
            style={{ maxWidth: '38px' }}
          />
        );
      case 'mastercard':
      case 'master':
        return (
          <img 
            src="/images/payment-methods/mastercard.png" 
            alt="Mastercard"
            className="h-5 w-auto object-contain"
            style={{ maxWidth: '38px' }}
          />
        );
      case 'american express':
      case 'amex':
        return (
          <img 
            src="/images/payment-methods/amex.png" 
            alt="American Express"
            className="h-5 w-auto object-contain"
            style={{ maxWidth: '38px' }}
          />
        );
      case 'discover':
        return (
          <img 
            src="/images/payment-methods/discover.png" 
            alt="Discover"
            className="h-5 w-auto object-contain"
            style={{ maxWidth: '38px' }}
          />
        );
      case 'jcb':
        return (
          <CreditCard className="w-5 h-5 text-purple-600" />
        );
      case 'diners':
      case 'diners club':
        return (
          <CreditCard className="w-5 h-5 text-blue-700" />
        );
      default:
        return (
          <CreditCard className="w-5 h-5 text-gray-500" />
        );
    }
  };

  const getPaymentMethodIcon = (paymentMethod) => {
    const methodLower = paymentMethod?.toLowerCase();
    
    // Use dynamically loaded logos from settings table
    if (paymentMethodLogos[methodLower]) {
      return (
        <img 
          src={paymentMethodLogos[methodLower]} 
          alt={paymentMethod}
          className="h-7 w-auto object-contain"
          style={{ maxWidth: '50px' }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      );
    }
    
    // Fallback badges for methods without logos
    switch (methodLower) {
      case 'google_pay':
      case 'google pay':
        return (
          <div className="w-8 h-5 bg-green-500 rounded text-white text-xs flex items-center justify-center font-bold">
            GP
          </div>
        );
      case 'bank_transfer':
        return (
          <div className="w-8 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">
            BT
          </div>
        );
      case 'crypto_wallet':
        return (
          <div className="w-8 h-5 bg-orange-500 rounded text-white text-xs flex items-center justify-center font-bold">
            CW
          </div>
        );
      default:
        return (
          <div className="w-8 h-5 bg-gradient-to-r from-blue-500 to-purple-600 rounded text-white text-xs flex items-center justify-center font-bold">
            <CreditCard className="h-3 w-3" />
          </div>
        );
    }
  };

  return (
    <div className="flex items-center">
      {(method === 'credit_card' || method === 'card') && brand ? getCardBrandIcon(brand) : getPaymentMethodIcon(method)}
    </div>
  );
};

const TransactionDetailsModal = ({ transaction, onClose, paymentMethodLogos = {} }) => {
  const [copied, setCopied] = useState('');

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'refunded':
        return <RefreshCw className="h-4 w-4 text-blue-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  if (!transaction) return null;

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Transaction Details
        </DialogTitle>
      </DialogHeader>
      
      <div className="grid gap-6 py-4">
        {/* Header Section */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {getStatusIcon(transaction.status)}
              <StatusBadge status={transaction.status} />
              <span className="text-sm text-muted-foreground">
                {new Date(transaction.created_at).toLocaleString()}
              </span>
            </div>
            <p className={`text-2xl font-bold ${transaction.amount < 0 ? 'text-red-600' : ''}`}>
              {formatCurrency(transaction.amount, transaction.currency)}
              {transaction.converted_amount && transaction.currency !== 'USD' && (
                <span className={`text-lg ml-2 ${transaction.amount < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                  (≈ {formatCurrency(transaction.converted_amount, 'USD')})
                </span>
              )}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Transaction ID</p>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                {transaction.id}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopy(transaction.id, 'id')}
              >
                {copied === 'id' ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Customer Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg border-b pb-2">Customer Information</h3>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Name</label>
              <p className="font-medium">{transaction.customer_name}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Email</label>
              <div className="flex items-center gap-2">
                <p className="text-sm">{transaction.customer_email}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(transaction.customer_email, 'email')}
                >
                  {copied === 'email' ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Country</label>
              <div className="mt-1">
                <Badge variant="outline" className="flex items-center gap-1.5 w-fit">
                  {getCountryAlpha2(transaction.country || transaction.customer_country) && (
                    <span
                      className={`fi fi-${getCountryAlpha2(transaction.country || transaction.customer_country)}`}
                      style={{ fontSize: '1.1rem', width: '1.1rem', height: '0.85rem' }}
                    ></span>
                  )}
                  {getCountryName(transaction.country || transaction.customer_country)}
                </Badge>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg border-b pb-2">Payment Information</h3>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Payment Method</label>
              <div className="flex items-center gap-2 mt-1">
                <PaymentMethodIcon method={transaction.payment_method} brand={transaction.card_brand} paymentMethodLogos={paymentMethodLogos} />
              </div>
            </div>
            
            {transaction.card_masked && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Card Information</label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-lg font-mono font-semibold text-gray-800 bg-gray-100 px-3 py-1 rounded">
                    {transaction.card_masked}
                  </span>
                  {transaction.card_brand && (
                    <Badge variant="outline" className="capitalize">
                      {transaction.card_brand}
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(transaction.card_masked, 'card')}
                  >
                    {copied === 'card' ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Payment Processor</label>
              <div className="mt-1">
                <Badge variant="secondary">
                  {transaction.payment_processor || 'N/A'}
                </Badge>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Currency</label>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="font-mono">
                  {transaction.currency}
                </Badge>
                {transaction.exchange_rate && (
                  <span className="text-xs text-muted-foreground">
                    (1 USD = {transaction.exchange_rate} {transaction.currency})
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* References Section */}
        <div className="space-y-4">
          <h3 className="font-semibold text-lg border-b pb-2">Reference Information</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Internal Reference</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-sm font-mono bg-muted px-2 py-1 rounded flex-1">
                  {transaction.reference || 'No reference'}
                </code>
                {transaction.reference && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(transaction.reference, 'reference')}
                  >
                    {copied === 'reference' ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                )}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Bank Reference</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-sm font-mono bg-muted px-2 py-1 rounded flex-1">
                  {transaction.bank_reference || 'No bank reference'}
                </code>
                {transaction.bank_reference && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(transaction.bank_reference, 'bank_reference')}
                  >
                    {copied === 'bank_reference' ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {transaction.processor_transaction_id && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Processor Transaction ID</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-sm font-mono bg-muted px-2 py-1 rounded flex-1">
                  {transaction.processor_transaction_id}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(transaction.processor_transaction_id, 'processor_id')}
                >
                  {copied === 'processor_id' ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Transaction Details */}
        <div className="space-y-4">
          <h3 className="font-semibold text-lg border-b pb-2">Enhanced Details</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Transaction Type</label>
              <div className="mt-1">
                <Badge variant="outline" className="capitalize">
                  {transaction.transaction_type || 'sale'}
                </Badge>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Approval Code</label>
              <div className="mt-1 font-mono text-sm">
                {transaction.approval_code || '-'}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">IP Address</label>
              <div className="mt-1 font-mono text-sm">
                {transaction.ip || 'N/A'}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">IP Country</label>
              <div className="mt-1 flex items-center gap-1.5">
                {transaction.ip_country && getCountryAlpha2(transaction.ip_country) && (
                  <span
                    className={`fi fi-${getCountryAlpha2(transaction.ip_country)}`}
                    style={{ fontSize: '1.1rem', width: '1.1rem', height: '0.85rem' }}
                  ></span>
                )}
                {transaction.ip_country ? getCountryName(transaction.ip_country) : 'Unknown'}
              </div>
            </div>
            
            {transaction.card_type && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Card Type</label>
                <div className="mt-1">
                  <Badge variant="outline" className="capitalize">
                    {transaction.card_type}
                  </Badge>
                </div>
              </div>
            )}
            
            {transaction.card_bank && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Card Bank</label>
                <div className="mt-1">
                  {transaction.card_bank}
                </div>
              </div>
            )}
            
            {transaction.card_country && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Card Country</label>
                <div className="mt-1">
                  <Badge variant="outline" className="flex items-center gap-1.5 w-fit">
                    {getCountryAlpha2(transaction.card_country) && (
                      <span
                        className={`fi fi-${getCountryAlpha2(transaction.card_country)}`}
                        style={{ fontSize: '1.1rem', width: '1.1rem', height: '0.85rem' }}
                      ></span>
                    )}
                    {getCountryName(transaction.card_country)}
                  </Badge>
                </div>
              </div>
            )}
            
            {transaction.risk_action && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Risk Action</label>
                <div className="mt-1">
                  <Badge 
                    variant={
                      transaction.risk_action === 'approve' ? 'success' :
                      transaction.risk_action === 'decline' ? 'destructive' :
                      transaction.risk_action === 'review' ? 'warning' :
                      'secondary'
                    }
                    className="capitalize"
                  >
                    {transaction.risk_action}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional Details */}
        <div className="space-y-4">
          <h3 className="font-semibold text-lg border-b pb-2">Additional Details</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <p className="mt-1">{transaction.description || 'No description'}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Created</label>
              <p className="mt-1">{new Date(transaction.created_at).toLocaleString()}</p>
            </div>
          </div>
          
          {transaction.updated_at !== transaction.created_at && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
              <p className="mt-1">{new Date(transaction.updated_at).toLocaleString()}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-2">
            {transaction.status === 'completed' && (
              <>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Download Receipt
                </Button>
                <Button variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Process Refund
                </Button>
              </>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </DialogContent>
  );
};

const TransactionHistory = () => {
  const { token, selectedPlatform, backendUrl, user } = useAuth();
  const reference = useReference();
  const { currentPlatform } = usePlatform();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Dynamic API URL for dev/prod environments
  const API = `${backendUrl}/api`;
  const { toast } = useToast();

  // Read initial filter state from URL params (persisted from Card view)
  const getInitialFilters = () => {
    const params = new URLSearchParams(location.search);
    return {
      status: params.get('status') ? params.get('status').split(',') : [],
      country: params.get('country') || 'all',
      currency: params.get('currency') || 'all',
      merchant_id: params.get('merchant_id') || 'all',
      result_code: params.get('result_code') ? params.get('result_code').split(',') : [],
      channel: params.get('channel') ? params.get('channel').split(',') : [],
    };
  };

  const getInitialPeriod = () => {
    const params = new URLSearchParams(location.search);
    return {
      period: params.get('period') || 'today',
      customDays: params.get('customDays') ? params.get('customDays').split(',').map(Number) : [],
      customMonth: params.get('customMonth') ? parseInt(params.get('customMonth')) : new Date().getMonth() + 1,
      customYear: params.get('customYear') ? parseInt(params.get('customYear')) : new Date().getFullYear(),
    };
  };

  const [searchTerm, setSearchTerm] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  });
  const [filters, setFilters] = useState(getInitialFilters);

  // Default the currency filter to the platform's configured default currency.
  // On first load we respect an explicit `currency` in the URL (deep-link from
  // Card view); otherwise we apply the platform default. On platform switch we
  // always reset to the new platform's default. User can still pick any value.
  const platformDefaultCurrency = (selectedPlatform && selectedPlatform !== 'all')
    ? (currentPlatform?.default_currency || 'all')
    : 'all';
  const hadInitialUrlCurrencyRef = useRef(new URLSearchParams(location.search).get('currency') != null);
  const prevPlatformRef = useRef(selectedPlatform);
  useEffect(() => {
    if (!selectedPlatform) return;
    if (!currentPlatform) return;
    const platformChanged = prevPlatformRef.current !== selectedPlatform;
    prevPlatformRef.current = selectedPlatform;
    if (platformChanged) {
      setFilters(prev => ({ ...prev, currency: platformDefaultCurrency }));
    } else if (!hadInitialUrlCurrencyRef.current) {
      setFilters(prev => ({ ...prev, currency: platformDefaultCurrency }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform, platformDefaultCurrency]);
  
  // Period filter state
  const [periodFilter, setPeriodFilter] = useState(getInitialPeriod);

  // Build URL search params from current filter state
  const buildFilterParams = () => {
    const params = new URLSearchParams();
    if (filters.status.length > 0) params.set('status', filters.status.join(','));
    if (filters.country && filters.country !== 'all') params.set('country', filters.country);
    if (filters.currency && filters.currency !== 'all') params.set('currency', filters.currency);
    if (filters.merchant_id && filters.merchant_id !== 'all') params.set('merchant_id', filters.merchant_id);
    if (filters.result_code.length > 0) params.set('result_code', filters.result_code.join(','));
    if (filters.channel.length > 0) params.set('channel', filters.channel.join(','));
    if (searchTerm) params.set('search', searchTerm.trim());
    params.set('period', periodFilter.period);
    if (periodFilter.period === 'custom') {
      params.set('customMonth', periodFilter.customMonth.toString());
      params.set('customYear', periodFilter.customYear.toString());
      if (periodFilter.customDays.length > 0) params.set('customDays', periodFilter.customDays.join(','));
    }
    return params.toString();
  };
  
  // Persist chosen Grid/Card view to backend (only if current default_screen is already a transaction view,
  // so we don't override an explicit Settings choice like Overview/Analytics/etc.)
  const persistViewPreference = async (targetScreen) => {
    try {
      const transactionScreens = ['/dashboard/transactions', '/dashboard/transactions-cards'];
      const currentDefault = user?.default_screen;
      if (currentDefault && transactionScreens.includes(currentDefault) && currentDefault !== targetScreen) {
        await axios.put(
          `${API}/users/me/default-screen`,
          { default_screen: targetScreen },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (e) {
      console.warn('[TransactionHistory] Failed to persist view preference:', e);
    }
  };

  // Auto-switch to Card view on tablet/mobile (< 1024px) once per session.
  // The Card view is much more readable on narrow screens. If the user later
  // chooses Grid manually via the toggle, that flag is set so we don't keep
  // bouncing them back to Cards.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const manualOverride = sessionStorage.getItem('txn-view-manual') === '1';
    if (!manualOverride && window.innerWidth < 1024) {
      const q = window.location.search || '';
      navigate(`/dashboard/transactions-cards${q}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Helper function to get number of days in a month
  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };
  

  // Fetch payment method logos from settings table
  const [paymentMethodLogos, setPaymentMethodLogos] = useState({});
  
  useEffect(() => {
    const fetchPaymentMethodLogos = async () => {
      if (!token) return;
      
      try {
        // Use public endpoint that works for all authenticated users
        const response = await axios.get(`${backendUrl}/api/settings/payment-methods/logos`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setPaymentMethodLogos(response.data.logos || {});
        console.log('[TransactionHistory] Payment method logos loaded:', response.data.logos);
      } catch (error) {
        console.error('[TransactionHistory] Error fetching payment method logos:', error);
      }
    };
    
    fetchPaymentMethodLogos();
  }, [token, backendUrl]);

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true); // For blocking overlay
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [error, setError] = useState(null);
  const [availableFilters, setAvailableFilters] = useState({
    countries: [],
    currencies: [],
    merchants: [],
    result_codes: [],
    statuses: [],
    channels: []
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 50,
    total: 0,
    total_pages: 0
  });
  const [totalAmount, setTotalAmount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Column management state
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [columnOrder, setColumnOrder] = useState(DEFAULT_COLUMNS.map(col => col.id));
  const [hasUserPreferences, setHasUserPreferences] = useState(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Load column preferences
  const loadColumnPreferences = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/user/column-preferences?component=transaction_grid`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.has_preferences) {
        const userColumnOrder = response.data.column_order;
        setColumnOrder(userColumnOrder);
        setHasUserPreferences(true);
        
        // Reorder columns based on user preferences
        const orderedColumns = userColumnOrder.map(id => 
          DEFAULT_COLUMNS.find(col => col.id === id)
        ).filter(Boolean);
        setColumns(orderedColumns);
      }
    } catch (err) {
      console.error('Error loading column preferences:', err);
    }
  };

  // Save column preferences
  const saveColumnPreferences = async (newOrder) => {
    if (!token) return;
    
    try {
      await axios.post(`${API}/user/column-preferences`, {
        component: 'transaction_grid',
        column_order: newOrder,
        hidden_columns: []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setHasUserPreferences(true);
      toast({
        title: "Column Order Saved",
        description: "Your column preferences have been saved.",
        variant: "default",
      });
    } catch (err) {
      console.error('Error saving column preferences:', err);
      toast({
        title: "Save Failed",
        description: "Failed to save column preferences.",
        variant: "destructive",
      });
    }
  };

  // Reset column preferences
  const resetColumnPreferences = async () => {
    if (!token) return;
    
    try {
      await axios.delete(`${API}/user/column-preferences?component=transaction_grid`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reset to default order
      setColumnOrder(DEFAULT_COLUMNS.map(col => col.id));
      setColumns(DEFAULT_COLUMNS);
      setHasUserPreferences(false);
      
      toast({
        title: "Columns Reset",
        description: "Column order has been reset to default.",
        variant: "default",
      });
    } catch (err) {
      console.error('Error resetting column preferences:', err);
      toast({
        title: "Reset Failed", 
        description: "Failed to reset column preferences.",
        variant: "destructive",
      });
    }
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id);
      const newIndex = columnOrder.indexOf(over.id);
      
      // Don't allow moving the actions column
      if (active.id === 'actions' || (newIndex === 0 && over.id !== 'actions')) {
        return;
      }
      
      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
      
      // Ensure actions stays first
      if (newOrder[0] !== 'actions') {
        const actionsIndex = newOrder.indexOf('actions');
        if (actionsIndex > -1) {
          newOrder.splice(actionsIndex, 1);
          newOrder.unshift('actions');
        }
      }
      
      setColumnOrder(newOrder);
      
      // Reorder columns array
      const newColumns = newOrder.map(id => 
        DEFAULT_COLUMNS.find(col => col.id === id)
      ).filter(Boolean);
      setColumns(newColumns);
      
      // Save preferences
      saveColumnPreferences(newOrder);
    }
  };

  useEffect(() => {
    if (token && selectedPlatform) {
      // Clear existing filters when platform changes to force refresh
      setAvailableFilters({
        countries: [],
        currencies: [],
        merchants: [],
        result_codes: [],
        statuses: [],
        channels: []
      });
      
      loadColumnPreferences();
      fetchTransactions();
    }
  }, [token, selectedPlatform]);

  useEffect(() => {
    if (token && selectedPlatform) {
      fetchTransactions();
    }
  }, [pagination.page, filters, periodFilter, token, selectedPlatform]);
  
  // Fetch filters AFTER initial transaction load (deferred)
  useEffect(() => {
    if (token && selectedPlatform && !initialLoading) {
      // Only fetch filters after initial data is loaded
      fetchAvailableFilters();
    }
  }, [initialLoading]);

  // Helper function to get date range based on period filter
  const getPeriodDateRange = () => {
    const now = new Date();
    
    // Helper function to format date as local datetime string without UTC conversion
    const formatLocalDateTime = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    };
    
    switch (periodFilter.period) {
      case 'today':
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        return {
          start_date: formatLocalDateTime(todayStart),
          end_date: formatLocalDateTime(todayEnd)
        };
        
      case 'last7days':
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const sevenDaysStart = new Date(sevenDaysAgo.getFullYear(), sevenDaysAgo.getMonth(), sevenDaysAgo.getDate());
        const todayEndLast7 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        return {
          start_date: formatLocalDateTime(sevenDaysStart),
          end_date: formatLocalDateTime(todayEndLast7)
        };
        
      case 'thismonth':
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        return {
          start_date: formatLocalDateTime(thisMonthStart),
          end_date: formatLocalDateTime(thisMonthEnd)
        };
        
      case 'thisyear':
        const thisYearStart = new Date(now.getFullYear(), 0, 1);
        const thisYearEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
        return {
          start_date: formatLocalDateTime(thisYearStart),
          end_date: formatLocalDateTime(thisYearEnd)
        };
        
      case 'custom':
        const customStart = new Date(periodFilter.customYear, periodFilter.customMonth - 1, 1);
        const customEnd = new Date(periodFilter.customYear, periodFilter.customMonth, 0, 23, 59, 59);
        return {
          period: 'custom',
          month: periodFilter.customMonth,
          year: periodFilter.customYear,
          days: periodFilter.customDays.length > 0 ? periodFilter.customDays.join(',') : null,
          start_date: formatLocalDateTime(customStart),
          end_date: formatLocalDateTime(customEnd)
        };
        
      default:
        return {};
    }
  };

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (pagination.page === 1) {
        fetchTransactions();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const fetchAvailableFilters = async () => {
    if (!token || !selectedPlatform) return;

    // Country list is data-driven (only countries present in the data). Currencies,
    // statuses and channels come from the DB Settings tables via ReferenceProvider.
    // Merchants and result codes are also data-driven.
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      };

      const [countriesRes, merchantsRes, resultCodesRes] = await Promise.all([
        axios.get(`${API}/filters/countries?platform_id=${selectedPlatform}&_t=${Date.now()}`, { headers }),
        axios.get(`${API}/filters/merchants?platform_id=${selectedPlatform}&_t=${Date.now()}`, { headers }),
        axios.get(`${API}/filters/result-codes?platform_id=${selectedPlatform}&_t=${Date.now()}`, { headers })
      ]);

      setAvailableFilters(prev => ({
        ...prev,
        countries: countriesRes.data.countries || [],
        merchants: merchantsRes.data.merchants || [],
        result_codes: resultCodesRes.data.result_codes || []
      }));
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  // Keep table-backed filter lists (currencies/statuses/channels) in sync with the
  // DB Settings tables via ReferenceProvider. (Country stays data-driven.)
  useEffect(() => {
    setAvailableFilters(prev => ({
      ...prev,
      currencies: (reference.currencies || []).map(c => c.code),
      statuses: (reference.statuses || []).map(s => s.code),
      channels: reference.channels || []
    }));
  }, [reference.currencies, reference.statuses, reference.channels]);

  const fetchTransactions = async () => {
    if (!token || !selectedPlatform) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        per_page: pagination.per_page.toString()
      });
      
      if (selectedPlatform && selectedPlatform !== 'all') {
        params.append('platform_id', selectedPlatform);
      }

      if (filters.status && filters.status.length > 0) {
        filters.status.forEach(status => params.append('status', status));
      }

      if (filters.country && filters.country !== 'all') {
        params.append('country', filters.country);
      }

      if (filters.currency && filters.currency !== 'all') {
        params.append('currency', filters.currency);
      }

      if (filters.merchant_id && filters.merchant_id !== 'all') {
        params.append('merchant_id', filters.merchant_id);
      }

      if (filters.result_code && filters.result_code.length > 0) {
        filters.result_code.forEach(code => params.append('result_code', code));
      }

      if (filters.channel && filters.channel.length > 0) {
        filters.channel.forEach(ch => params.append('channel', ch));
      }


      if (searchTerm) {
        params.append('search', searchTerm.trim());
      }

      // Add period filter parameters
      const dateRange = getPeriodDateRange();
      if (dateRange.start_date) {
        params.append('start_date', dateRange.start_date);
      }
      if (dateRange.end_date) {
        params.append('end_date', dateRange.end_date);
      }
      if (dateRange.period) {
        params.append('period', dateRange.period);
      }
      if (dateRange.month) {
        params.append('month', dateRange.month);
      }
      if (dateRange.year) {
        params.append('year', dateRange.year);
      }
      if (dateRange.days) {
        params.append('days', dateRange.days);
      }

      // Fetch current page transactions
      const response = await axios.get(`${API}/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      setTransactions(data.transactions || []);
      setPagination({
        page: data.page,
        per_page: data.per_page,
        total: data.total,
        total_pages: data.total_pages
      });
      // Note: we intentionally do NOT call notifySidebarCount('transactions', ...)
      // here — the sidebar's transactions count should reflect the all-time
      // platform total, not the page's currently-filtered total.

      // Fetch total amount efficiently using aggregation endpoint
      const totalParams = new URLSearchParams();
      
      if (selectedPlatform && selectedPlatform !== 'all') {
        totalParams.append('platform_id', selectedPlatform);
      }

      if (filters.status && filters.status.length > 0) {
        filters.status.forEach(status => totalParams.append('status', status));
      }

      if (filters.country && filters.country !== 'all') {
        totalParams.append('country', filters.country);
      }

      if (filters.currency && filters.currency !== 'all') {
        totalParams.append('currency', filters.currency);
      }

      if (filters.merchant_id && filters.merchant_id !== 'all') {
        totalParams.append('merchant_id', filters.merchant_id);
      }

      if (filters.result_code && filters.result_code.length > 0) {
        filters.result_code.forEach(code => totalParams.append('result_code', code));
      }

      if (filters.channel && filters.channel.length > 0) {
        filters.channel.forEach(ch => totalParams.append('channel', ch));
      }


      if (searchTerm) {
        totalParams.append('search', searchTerm.trim());
      }

      // Add period filter parameters
      const totalDateRange = getPeriodDateRange();
      if (totalDateRange.start_date) {
        totalParams.append('start_date', totalDateRange.start_date);
      }
      if (totalDateRange.end_date) {
        totalParams.append('end_date', totalDateRange.end_date);
      }
      if (totalDateRange.period) {
        totalParams.append('period', totalDateRange.period);
      }
      if (totalDateRange.month) {
        totalParams.append('month', totalDateRange.month);
      }
      if (totalDateRange.year) {
        totalParams.append('year', totalDateRange.year);
      }
      if (totalDateRange.days) {
        totalParams.append('days', totalDateRange.days);
      }
      
      // Get totals efficiently using aggregation
      const totalsResponse = await axios.get(`${API}/transactions/totals?${totalParams}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTotalAmount(totalsResponse.data.total_amount || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
      setInitialLoading(false); // Disable blocking overlay after first load
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: [],
      country: 'all',
      currency: 'all',
      merchant_id: 'all',
      result_code: []
    });
  };

  const handleExport = async (format = 'csv') => {
    try {
      setExportLoading(true);
      
      // Build export parameters
      const exportDateRange = getPeriodDateRange();
      const params = new URLSearchParams();
      
      if (selectedPlatform && selectedPlatform !== 'all') {
        params.append('platform_id', selectedPlatform);
      }

      if (filters.status && filters.status.length > 0) {
        filters.status.forEach(status => params.append('status', status));
      }

      if (filters.country && filters.country !== 'all') {
        params.append('country', filters.country);
      }

      if (filters.currency && filters.currency !== 'all') {
        params.append('currency', filters.currency);
      }

      if (filters.merchant_id && filters.merchant_id !== 'all') {
        params.append('merchant_id', filters.merchant_id);
      }

      if (filters.result_code && filters.result_code.length > 0) {
        filters.result_code.forEach(code => params.append('result_code', code));
      }
      
      // Add channel filter
      if (filters.channel && filters.channel.length > 0) {
        filters.channel.forEach(ch => params.append('channel', ch));
      }

      if (searchTerm) {
        params.append('search', searchTerm.trim());
      }

      // Add period filter parameters
      if (exportDateRange.start_date) {
        params.append('start_date', exportDateRange.start_date);
      }
      if (exportDateRange.end_date) {
        params.append('end_date', exportDateRange.end_date);
      }
      
      // Add specific days filter from custom period.
      // NOTE: /transactions and /transactions/totals read the `days` (comma-separated)
      // param, while /transactions/export/excel|csv read the repeated `specific_days`
      // param. The export flow reuses these `params` for ALL THREE calls — the count
      // pre-check (/totals), the server-side export, and the client-side /transactions
      // fetch (JSON/PDF/small-CSV) — so we MUST send BOTH conventions, otherwise the
      // custom-day filter is silently dropped and the whole month gets exported.
      if (periodFilter.period === 'custom' && periodFilter.customDays && periodFilter.customDays.length > 0) {
        periodFilter.customDays.forEach(day => params.append('specific_days', day));
        params.append('days', periodFilter.customDays.join(','));
      }

      // Pass the user's grid column order so the export columns match the grid layout.
      const orderedFields = buildExportFieldsFromColumnOrder(columnOrder);
      if (orderedFields.length > 0) {
        params.append('columns', orderedFields.join(','));
      }

      // Check total count first
      const countResponse = await axios.get(`${API}/transactions/totals?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const totalCount = countResponse.data.total_count || 0;
      const EXPORT_LIMIT = 100000; // Increased to 100k for server-side exports

      // Show warning if exceeds limit
      if (totalCount > EXPORT_LIMIT) {
        const proceed = window.confirm(
          `Warning: Your filtered data contains ${totalCount.toLocaleString()} transactions.\n\n` +
          `Export is limited to ${EXPORT_LIMIT.toLocaleString()} records.\n\n` +
          `Only the first ${EXPORT_LIMIT.toLocaleString()} transactions will be exported.\n\n` +
          `Please use additional filters to reduce the dataset, or click OK to proceed with the limited export.`
        );
        
        if (!proceed) {
          setExportLoading(false);
          return;
        }
      }

      // For Excel (xlsx), always use server-side export for proper .xlsx format
      // For CSV with large datasets, also use server-side export
      if (format === 'xlsx' || (format === 'csv' && totalCount > 5000)) {
        params.append('limit', Math.min(totalCount, EXPORT_LIMIT).toString());
        
        const exportEndpoint = format === 'xlsx' 
          ? `${API}/transactions/export/excel?${params}`
          : `${API}/transactions/export/csv?${params}`;
        
        toast({
          title: "Preparing Export",
          description: `Generating ${format.toUpperCase()} file with ${Math.min(totalCount, EXPORT_LIMIT).toLocaleString()} transactions. This may take a moment...`,
        });
        
        // Use fetch for streaming download
        const response = await fetch(exportEndpoint, {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}`,
          }
        });
        
        if (!response.ok) {
          throw new Error(`Export failed: ${response.statusText}`);
        }
        
        // Get the blob from response
        const blob = await response.blob();
        
        // Extract filename from Content-Disposition header or generate one
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `transactions_export_${new Date().toISOString().split('T')[0]}`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename=(.+)/);
          if (filenameMatch) {
            filename = filenameMatch[1].replace(/"/g, '');
          }
        }
        
        // Get total exported count from header
        const exportedCount = response.headers.get('X-Total-Exported') || totalCount;
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename.endsWith(`.${format}`) ? filename : `${filename}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        toast({
          title: "Export Complete",
          description: `Successfully exported ${Number(exportedCount).toLocaleString()} transactions as ${format.toUpperCase()}.`,
          variant: "default",
        });
        
        setExportLoading(false);
        return;
      }

      // For smaller datasets or JSON/PDF, use client-side generation
      params.append('per_page', Math.min(totalCount, EXPORT_LIMIT).toString());

      // Fetch transactions matching the current filters
      const response = await axios.get(`${API}/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allTransactions = response.data.transactions;
      
      // Field catalog: label (human-readable header) + getValue (raw extractor from a transaction doc)
      // Order: handled dynamically via `orderedFields` (derived from the user's grid column order above).
      const FIELD_CATALOG = {
        transaction_date: { label: 'Transaction Date', getValue: t => t.created_at || '' },
        transaction_type: { label: 'Transaction Type', getValue: t => t.transaction_type || '' },
        channel: { label: 'Channel', getValue: t => t.channel || 'Web' },
        reference: { label: 'Reference', getValue: t => t.reference || '' },
        description: { label: 'Description', getValue: t => t.description || '' },
        amount: { label: 'Amount', getValue: t => t.amount || 0 },
        currency: { label: 'Currency', getValue: t => t.currency || '' },
        converted_amount: { label: 'Converted Amount', getValue: t => t.converted_amount || 0 },
        converted_currency: { label: 'Converted Currency', getValue: t => t.converted_currency || '' },
        status: { label: 'Status', getValue: t => t.status || '' },
        result_code: { label: 'Result Code', getValue: t => t.result_code || '' },
        customer_name: { label: 'Customer Name', getValue: t => t.customer_name || '' },
        customer_email: { label: 'Customer Email', getValue: t => t.customer_email || '' },
        customer_country: { label: 'Customer Country', getValue: t => t.customer_country || '' },
        payment_method: { label: 'Payment Method', getValue: t => t.payment_method || '' },
        card_masked: { label: 'Card Number', getValue: t => t.card_masked || '' },
        card_brand: { label: 'Card Brand', getValue: t => t.card_brand || '' },
        card_type: { label: 'Card Type', getValue: t => t.card_type || '' },
        card_bank: { label: 'Card Bank', getValue: t => t.card_bank || '' },
        card_country: { label: 'Card Country', getValue: t => t.card_country || '' },
        approval_code: { label: 'Approval Code', getValue: t => t.approval_code || '' },
        ip: { label: 'IP', getValue: t => t.ip || '' },
        ip_country: { label: 'IP Country', getValue: t => t.ip_country || '' },
        risk_action: { label: 'Risk Action', getValue: t => t.risk_action || '' },
        risk_score: { label: 'Risk Score', getValue: t => t.risk_score || '' },
        platform_id: { label: 'Platform ID', getValue: t => t.platform_id || '' },
        payment_processor: { label: 'Payment Processor', getValue: t => t.processor || '' },
        merchant_name: { label: 'Merchant Name', getValue: t => t.merchant_name || '' },
        id: { label: 'Transaction ID', getValue: t => t.id || '' },
        transaction_id: { label: 'External Transaction ID', getValue: t => t.transaction_id || '' },
        bank_reference: { label: 'Bank Reference', getValue: t => t.bank_reference || '' },
        country: { label: 'Country', getValue: t => t.country || '' },
      };

      // Use the user's grid column order if available; fall back to the full catalog order.
      const exportFields = orderedFields.length > 0
        ? orderedFields.filter(f => FIELD_CATALOG[f])
        : Object.keys(FIELD_CATALOG);

      const csvHeaders = exportFields.map(f => FIELD_CATALOG[f].label);
      const jsonRows = allTransactions.map(t => {
        const row = {};
        for (const f of exportFields) row[f] = FIELD_CATALOG[f].getValue(t);
        return row;
      });
      const csvRows = allTransactions.map(t => exportFields.map(f => FIELD_CATALOG[f].getValue(t)));

      // Generate filename base
      let filename = 'transactions';
      if (filters.status && filters.status !== 'all') filename += `_${filters.status}`;
      if (filters.country && filters.country !== 'all') filename += `_${filters.country}`;
      if (filters.currency && filters.currency !== 'all') filename += `_${filters.currency}`;
      if (filters.merchant_id && filters.merchant_id !== 'all') filename += `_merchant`;
      if (filters.result_code && filters.result_code !== 'all') filename += `_result`;
      if (searchTerm) filename += `_search-${searchTerm.replace(/[^a-zA-Z0-9]/g, '')}`;
      filename += `_${new Date().toISOString().split('T')[0]}`;

      let blob, mimeType, extension;

      if (format === 'json') {
        // JSON Export - uses database field names for easy re-import
        const jsonContent = JSON.stringify(jsonRows, null, 2);
        blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
        mimeType = 'application/json';
        extension = 'json';
      } else if (format === 'pdf') {
        // PDF Export - Generate HTML table and print to PDF
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          const pdfContent = `
            <!DOCTYPE html>
            <html>
            <head>
              <title>Transaction Export</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                h1 { color: #333; font-size: 18px; margin-bottom: 20px; }
                table { border-collapse: collapse; width: 100%; font-size: 10px; }
                th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
                th { background-color: #4472C4; color: white; font-weight: bold; }
                tr:nth-child(even) { background-color: #f9f9f9; }
                .footer { margin-top: 20px; font-size: 10px; color: #666; }
                @media print {
                  body { margin: 0; }
                  table { page-break-inside: auto; }
                  tr { page-break-inside: avoid; }
                }
              </style>
            </head>
            <body>
              <h1>Transaction Export - ${new Date().toLocaleDateString()}</h1>
              <table>
                <thead>
                  <tr>${csvHeaders.map(h => `<th>${h}</th>`).join('')}</tr>
                </thead>
                <tbody>
                  ${csvRows.map(row => `<tr>${row.map(cell => `<td>${cell || ''}</td>`).join('')}</tr>`).join('')}
                </tbody>
              </table>
              <div class="footer">
                Total Records: ${allTransactions.length.toLocaleString()} | Generated: ${new Date().toLocaleString()}
              </div>
              <script>
                window.onload = function() {
                  window.print();
                  window.onafterprint = function() { window.close(); }
                }
              </script>
            </body>
            </html>
          `;
          printWindow.document.write(pdfContent);
          printWindow.document.close();
          
          toast({
            title: "PDF Ready",
            description: `Printing ${allTransactions.length.toLocaleString()} transactions. Use your browser's print dialog to save as PDF.`,
            variant: "default",
          });
          
          setExportLoading(false);
          return;
        }
      } else {
        // CSV Export (default) - client-side for small datasets
        const escapeCsvValue = (value) => {
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        };
        
        const csvContent = [
          csvHeaders.map(escapeCsvValue).join(','),
          ...csvRows.map(row => row.map(escapeCsvValue).join(','))
        ].join('\n');
        
        blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        mimeType = 'text/csv';
        extension = 'csv';
      }

      // Download file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      // Show success message with count
      const recordCount = allTransactions.length;
      
      toast({
        title: "Export Complete",
        description: `Successfully exported ${recordCount.toLocaleString()} transactions as ${extension.toUpperCase()}.`,
        variant: "default",
      });
      
    } catch (err) {
      console.error('Error exporting transactions:', err);
      setError('Failed to export transactions');
      toast({
        title: "Export Failed",
        description: "There was an error exporting the transactions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchTransactions();
  };

  const handleDeleteClick = (transaction) => {
    setTransactionToDelete(transaction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!transactionToDelete) return;
    
    try {
      setDeleting(true);
      
      await axios.delete(`${API}/transactions/${transactionToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Transaction Deleted",
        description: `Transaction ${transactionToDelete.transaction_id} has been deleted and logged.`,
        variant: "default",
      });
      
      // Refresh the transactions list
      fetchTransactions();
      
      // Close dialog
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
      
    } catch (err) {
      console.error('Error deleting transaction:', err);
      toast({
        title: "Delete Failed",
        description: err.response?.data?.detail || "Failed to delete transaction. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  // Render cell content based on column type
  const renderCellContent = (column, transaction) => {
    switch (column.id) {
      case 'actions':
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <Dialog>
                <DialogTrigger asChild>
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </DropdownMenuItem>
                </DialogTrigger>
                <TransactionDetailsModal 
                  transaction={transaction}
                  onClose={() => {}}
                  paymentMethodLogos={paymentMethodLogos}
                />
              </Dialog>
              <DropdownMenuItem>
                <Download className="h-4 w-4 mr-2" />
                Download Receipt
              </DropdownMenuItem>
              {transaction.status === 'completed' && (
                <DropdownMenuItem>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refund
                </DropdownMenuItem>
              )}
              {user?.is_admin && (
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                  onSelect={() => handleDeleteClick(transaction)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Transaction
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      
      case 'transaction_id':
        return (
          <span className="font-mono text-sm">
            {transaction.transaction_id || transaction.id}
          </span>
        );
      
      case 'customer':
        return (
          <div>
            <div className="font-medium">{transaction.customer_name}</div>
            <div className="text-sm text-muted-foreground">
              <span>{transaction.customer_email}</span>
            </div>
          </div>
        );
      
      case 'amount':
        const isNegativeAmount = transaction.amount < 0;
        return (
          <div className={`font-semibold text-right ${isNegativeAmount ? 'text-red-600' : ''}`}>
            {formatCurrency(transaction.amount, transaction.currency)}
            {transaction.converted_amount && transaction.currency !== 'USD' && (
              <div className={`text-xs ${isNegativeAmount ? 'text-red-400' : 'text-muted-foreground'}`}>
                ≈ {formatCurrency(transaction.converted_amount, 'USD')}
              </div>
            )}
          </div>
        );
      
      case 'payment_method':
        return (
          <div className="flex items-center justify-center">
            <PaymentMethodIcon method={transaction.payment_method} brand={transaction.card_brand} paymentMethodLogos={paymentMethodLogos} />
          </div>
        );
      
      case 'card':
        return (
          <div className="font-mono text-sm">
            {transaction.card_masked ? (
              <div className="flex flex-col">
                <span className="font-semibold text-gray-800">
                  {transaction.card_masked}
                </span>
                {transaction.card_country && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <span 
                      className={`fi fi-${transaction.card_country.toLowerCase()}`}
                      style={{ fontSize: '1.1rem', width: '1.1rem', height: '0.85rem' }}
                    ></span>
                    <span className="text-xs text-gray-500">
                      {getCountryName(transaction.card_country)}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <span className="text-muted-foreground text-xs">No card data</span>
            )}
          </div>
        );
      
      case 'processor':
        return (
          <Badge variant="secondary" className="text-xs">
            {transaction.payment_processor || 'N/A'}
          </Badge>
        );
      
      case 'merchant':
        return (
          <div className="text-sm">
            {transaction.merchant_name || 'N/A'}
            {transaction.merchant_group && (
              <div className="text-xs text-muted-foreground">
                {transaction.merchant_group.replace('_', ' ')}
              </div>
            )}
          </div>
        );
      
      case 'platform':
        return (
          <Badge variant="outline" className="text-xs">
            {transaction.platform_name || 'Unknown Platform'}
          </Badge>
        );
      
      case 'reference':
        return (
          <div className="text-sm">
            {/* Reference number */}
            {transaction.reference && (
              <div className="font-mono text-sm font-semibold mb-1">
                {transaction.reference}
              </div>
            )}
            {/* Description */}
            <div className="text-xs text-muted-foreground">
              {transaction.description || transaction.memo || 'No description'}
            </div>
          </div>
        );
      
      case 'transaction_type':
        const channelInfo = getChannelIcon(transaction.channel);
        const ChannelIcon = channelInfo.icon;
        
        return (
          <div className="space-y-1">
            {/* Transaction Type */}
            <Badge variant="outline" className="text-xs capitalize">
              {transaction.transaction_type || 'sale'}
            </Badge>
            {/* Channel with colored icon */}
            <div className="flex items-center gap-1.5">
              <ChannelIcon className={`h-3.5 w-3.5 ${channelInfo.color}`} />
              <span className="text-xs text-muted-foreground">
                {channelInfo.label}
              </span>
            </div>
          </div>
        );
      
      case 'approval_code':
        return (
          <div className="font-mono text-sm">
            {/* Reference code */}
            {transaction.bank_reference && (
              <div className="text-xs text-muted-foreground mb-1">
                {transaction.bank_reference}
              </div>
            )}
            {/* Approval code */}
            <div className="font-semibold">
              {transaction.bank_approval_code || transaction.approval_code || '-'}
            </div>
          </div>
        );
      
      case 'ip_info':
        return (
          <div className="text-sm">
            <div className="font-mono text-xs font-semibold text-gray-800">
              {transaction.ip || 'N/A'}
            </div>
            {transaction.ip_country && (
              <div className="flex items-center gap-1.5 mt-1">
                <span 
                  className={`fi fi-${transaction.ip_country.toLowerCase()}`}
                  style={{ fontSize: '1.1rem', width: '1.1rem', height: '0.85rem' }}
                ></span>
                <span className="text-xs text-gray-500">
                  {getCountryName(transaction.ip_country)}
                </span>
              </div>
            )}
          </div>
        );
      
      case 'risk_action':
        const isDeclined = transaction.risk_action === 'decline';
        const isApprove = transaction.risk_action === 'approve';
        const isReview = transaction.risk_action === 'review';
        return (
          <Badge 
            variant={isDeclined ? 'outline' : isApprove ? 'success' : isReview ? 'warning' : 'secondary'}
            className={`text-xs capitalize ${
              isDeclined ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' : ''
            }`}
          >
            {transaction.risk_action || 'unknown'}
          </Badge>
        );
      
      case 'platform_processor':
        return (
          <div className="text-sm">
            {/* Platform */}
            <div className="font-semibold mb-1">
              {transaction.platform_id || 'N/A'}
            </div>
            {/* Processor */}
            <div className="text-xs text-muted-foreground">
              {transaction.processor || transaction.payment_processor || 'N/A'}
            </div>
          </div>
        );
      
      case 'merchant':
        return (
          <div className="text-sm">
            {transaction.merchant_name || 'N/A'}
          </div>
        );
      
      case 'status':
        return (
          <div className="flex flex-col gap-1">
            <StatusBadge status={transaction.status} />
            {transaction.result_code && (
              <ResultCodeBadge resultCode={transaction.result_code} resultCodes={availableFilters.result_codes} />
            )}
          </div>
        );
      
      case 'date':
        return (
          <div>
            {new Date(transaction.created_at).toLocaleDateString()}
            <div className="text-xs text-muted-foreground">
              {new Date(transaction.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 p-6 relative">
      {/* Full-screen loading overlay for initial load */}
      <LoadingOverlay 
        show={initialLoading}
        message="Loading Transaction History"
        subMessage="Fetching your transaction data from the database..."
      />
      
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2" style={{ color: 'var(--input-text-color)' }}>
              <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
              Transaction History
            </h1>
            <p className="text-muted-foreground mt-1 sm:mt-2 text-sm">
              View and manage all payment transactions
            </p>
          </div>
          {/* View Toggle - positioned next to title */}
          <div className="flex items-center gap-1 bg-muted rounded-xl p-1.5" data-testid="view-toggle">
            <button
              onClick={() => { persistViewPreference('/dashboard/transactions'); navigate('/dashboard/transactions'); }}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all bg-background shadow-sm text-foreground"
              data-testid="view-toggle-grid"
              title="Table View"
            >
              <LayoutList className="h-4 w-4 lg:h-5 lg:w-5 text-blue-500" />
              Grid
            </button>
            <button
              onClick={() => { const q = buildFilterParams(); persistViewPreference('/dashboard/transactions-cards'); navigate(`/dashboard/transactions-cards${q ? '?' + q : ''}`); }}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all text-muted-foreground hover:text-foreground"
              data-testid="view-toggle-card"
              title="Card View"
            >
              <LayoutGrid className="h-4 w-4 lg:h-5 lg:w-5 text-gray-400" />
              Card
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
        {/* Single Transaction Summary Card */}
        <Card className="w-auto sm:w-56 shrink-0">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <CreditCard className="h-4 w-4" />
              Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4 px-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Count:</span>
                <span className="text-base font-semibold" style={{ color: 'var(--input-text-color)' }}>{formatNumber(pagination.total)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total:</span>
                <span className="text-base font-semibold" style={{ color: 'var(--input-text-color)' }}>
                  {formatCurrency(
                    totalAmount, 
                    // Use selected currency filter or default to USD for "all currencies"
                    filters.currency && filters.currency !== 'all' 
                      ? filters.currency 
                      : 'USD'
                  )}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          {/* Filter Section - Reorganized for better UX */}
          <div className="space-y-4">
            {/* Row 1: Period and Search */}
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
              {/* Period Filter - First Position */}
              <div className="flex items-center flex-wrap gap-2">
                <Select value={periodFilter.period} onValueChange={(value) => setPeriodFilter(prev => ({ ...prev, period: value }))}>
                  <SelectTrigger className="w-36">
                    <Calendar className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="last7days">Last 7 Days</SelectItem>
                    <SelectItem value="thismonth">This Month</SelectItem>
                    <SelectItem value="thisyear">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Custom Month/Year Selectors */}
                {periodFilter.period === 'custom' && (
                  <>
                    {/* Days Multi-Select - Before Month */}
                    <MultiSelect
                      options={['All Days', ...Array.from({ length: getDaysInMonth(periodFilter.customMonth, periodFilter.customYear) }, (_, i) => (i + 1).toString())]}
                      value={periodFilter.customDays.length === 0 ? ['All Days'] : periodFilter.customDays.map(d => d.toString())}
                      onChange={(value) => {
                        if (value.includes('All Days') && value.length > 1) {
                          // If All Days is clicked while other days are selected, clear all
                          if (periodFilter.customDays.length > 0) {
                            setPeriodFilter(prev => ({ ...prev, customDays: [] }));
                          } else {
                            // Otherwise, select only the specific days
                            setPeriodFilter(prev => ({ ...prev, customDays: value.filter(v => v !== 'All Days').map(d => parseInt(d)) }));
                          }
                        } else if (value.includes('All Days') && value.length === 1) {
                          setPeriodFilter(prev => ({ ...prev, customDays: [] }));
                        } else {
                          setPeriodFilter(prev => ({ ...prev, customDays: value.map(d => parseInt(d)) }));
                        }
                      }}
                      placeholder="All Days"
                      className="w-32"
                      maxDisplayItems={1}
                      renderOption={(day) => day}
                      renderValue={(day) => day === 'All Days' ? 'All Days' : `Day ${day}`}
                      dataTestid="period-days-trigger"
                      optionTestidPrefix="period-day-option"
                    />
                    
                    <Select 
                      value={periodFilter.customMonth.toString()} 
                      onValueChange={(value) => setPeriodFilter(prev => ({ ...prev, customMonth: parseInt(value), customDays: [] }))}
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">January</SelectItem>
                        <SelectItem value="2">February</SelectItem>
                        <SelectItem value="3">March</SelectItem>
                        <SelectItem value="4">April</SelectItem>
                        <SelectItem value="5">May</SelectItem>
                        <SelectItem value="6">June</SelectItem>
                        <SelectItem value="7">July</SelectItem>
                        <SelectItem value="8">August</SelectItem>
                        <SelectItem value="9">September</SelectItem>
                        <SelectItem value="10">October</SelectItem>
                        <SelectItem value="11">November</SelectItem>
                        <SelectItem value="12">December</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select 
                      value={periodFilter.customYear.toString()} 
                      onValueChange={(value) => setPeriodFilter(prev => ({ ...prev, customYear: parseInt(value), customDays: [] }))}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 5 }, (_, i) => {
                          const year = new Date().getFullYear() - i;
                          return (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
              
              {/* Search Box */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-foreground/50" />
                  <Input
                    placeholder="Search exact value: transaction ID, reference, email, name, card…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full"
                    data-testid="transaction-search-input"
                  />
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center flex-wrap gap-2 sm:ml-auto">
                {(filters.country && filters.country !== 'all' || filters.currency && filters.currency !== 'all' || filters.merchant_id && filters.merchant_id !== 'all' || filters.result_code?.length > 0 || filters.status?.length > 0 || filters.channel?.length > 0) && (
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
                
                <Button onClick={handleRefresh} variant="outline" size="sm" disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                
                {/* Export Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={exportLoading || (!loading && transactions.length === 0)} data-testid="export-menu-button">
                      <Download className={`h-4 w-4 mr-1 ${exportLoading ? 'animate-pulse' : ''}`} />
                      {exportLoading ? 'Exporting...' : 'Export'}
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleExport('csv')} disabled={exportLoading} data-testid="export-csv">
                      <FileText className="h-4 w-4 mr-2" />
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('xlsx')} disabled={exportLoading} data-testid="export-excel">
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Export as Excel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('pdf')} disabled={exportLoading} data-testid="export-pdf">
                      <FileType className="h-4 w-4 mr-2" />
                      Export as PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* Column Toggle */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Columns className="h-4 w-4 mr-1" />
                      Columns
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 max-h-96 overflow-y-auto">
                    <DropdownMenuLabel>Column Order</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={resetColumnPreferences}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset Column Order
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            
            {/* Row 2: Data Filters */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Status Filter */}
              <MultiSelect
                options={[
                  'All', 
                  ...[...new Set(availableFilters.statuses)].sort((a, b) => a.localeCompare(b))
                ]}
                value={filters.status}
                onChange={(value) => {
                  if (value.includes('All')) {
                    handleFilterChange('status', []);
                  } else {
                    handleFilterChange('status', value);
                  }
                }}
                placeholder="All Status"
                className="w-40"
                maxDisplayItems={1}
                renderOption={(status) => {
                  if (status === 'All') return 'All Status';
                  return status.charAt(0).toUpperCase() + status.slice(1).replace(/-/g, ' ');
                }}
                renderValue={(status) => {
                  if (status === 'All') return 'All Status';
                  return status.charAt(0).toUpperCase() + status.slice(1).replace(/-/g, ' ');
                }}
              />
              
              {/* Channel Filter - MultiSelect */}
              <MultiSelect
                options={[
                  { code: 'All', name: 'All Channels' },
                  ...(availableFilters.channels || [])
                    .slice()
                    .sort((a, b) => (a.name || a.code || '').localeCompare(b.name || b.code || ''))
                ]}
                value={filters.channel}
                onChange={(value) => {
                  if (value.includes('All')) {
                    handleFilterChange('channel', []);
                  } else {
                    handleFilterChange('channel', value);
                  }
                }}
                placeholder="All Channels"
                className="w-40"
                maxDisplayItems={1}
                dataTestid="transactions-channel-filter"
                optionTestidPrefix="txn-channel"
                renderOption={(channel) => {
                  if (channel.code === 'All') return 'All Channels';
                  return channel.name || channel.code;
                }}
                renderValue={(channel) => {
                  if (channel.code === 'All') return 'All Channels';
                  return channel.name || channel.code;
                }}
              />

              {/* Merchant Filter */}
              <MerchantSearchSelect
                merchants={availableFilters.merchants}
                value={filters.merchant_id || "all"}
                onChange={(value) => handleFilterChange('merchant_id', value === "all" ? '' : value)}
                placeholder="All Merchants"
                className="w-44"
              />
              
              {/* Country Filter */}
              <Select value={filters.country || "all"} onValueChange={(value) => handleFilterChange('country', value === "all" ? '' : value)}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All Countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Countries</SelectItem>
                  {availableFilters.countries
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Currency Filter */}
              <Select value={filters.currency || "all"} onValueChange={(value) => handleFilterChange('currency', value === "all" ? '' : value)}>
                <SelectTrigger className="w-32" data-testid="transactions-currency-filter">
                  <SelectValue placeholder="All">
                    {filters.currency && filters.currency !== 'all' 
                      ? formatCurrencySelector(filters.currency)
                      : "All Currencies"
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Currencies</SelectItem>
                  {availableFilters.currencies
                    .slice()
                    .sort((a, b) => a.localeCompare(b))
                    .map((currency) => (
                    <SelectItem key={currency} value={currency}>
                      {formatCurrencySelector(currency)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Result Code Filter */}
              <MultiSelect
                options={[
                  { code: 'All', name: 'All Results' },
                  ...availableFilters.result_codes
                    .filter((item, index, self) => 
                      index === self.findIndex(t => t.code === item.code)
                    )
                    .filter(item => item.code && item.code !== '-' && item.code !== '0.0')
                    .sort((a, b) => a.code.localeCompare(b.code))
                ]}
                value={filters.result_code}
                onChange={(value) => {
                  if (value.includes('All')) {
                    handleFilterChange('result_code', []);
                  } else {
                    handleFilterChange('result_code', value);
                  }
                }}
                placeholder="All Results"
                className="w-44"
                maxDisplayItems={1}
                renderOption={(resultCode) => {
                  if (resultCode.code === 'All') return 'All Results';
                  const displayName = resultCode.name || resultCode.code;
                  const cleanName = displayName.startsWith(resultCode.code)
                    ? displayName.slice(resultCode.code.length).replace(/^[\s-]+/, '')
                    : displayName;
                  return cleanName && cleanName !== resultCode.code
                    ? `${resultCode.code}: ${cleanName}`
                    : resultCode.code;
                }}
                renderValue={(resultCode) => {
                  if (resultCode.code === 'All') return 'All Results';
                  const displayName = resultCode.name || resultCode.code;
                  const cleanName = displayName.startsWith(resultCode.code)
                    ? displayName.slice(resultCode.code.length).replace(/^[\s-]+/, '')
                    : displayName;
                  return cleanName && cleanName !== resultCode.code
                    ? `${resultCode.code}: ${cleanName}`
                    : resultCode.code;
                }}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={handleRefresh} variant="outline">
                Try Again
              </Button>
            </div>
          ) : (
            <>
              <div className="relative">
                {/* Table Loading Overlay for subsequent loads (pagination, filtering) */}
                <TableLoadingOverlay 
                  show={loading && !initialLoading}
                  message="Updating transactions..."
                  rowCount={10}
                />
                
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                            {columns.map((column) => (
                              <DraggableHeader
                                key={column.id}
                                column={column}
                                isFixed={column.fixed}
                              />
                            ))}
                          </SortableContext>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.map((transaction) => (
                          <TableRow key={transaction.id} className="hover:bg-muted/50 h-16">
                            {columns.map((column) => (
                              <TableCell key={`${transaction.id}-${column.id}`} className={`${column.width} py-3`}>
                                {renderCellContent(column, transaction)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </DndContext>
              </div>

              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.total_pages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page <= 1 || loading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page >= pagination.total_pages || loading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Transaction</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this transaction? This action cannot be undone.
              The transaction will be removed from the system and logged for audit purposes.
              {transactionToDelete && (
                <div className="mt-4 p-3 bg-muted rounded-md">
                  <p className="text-sm font-medium">Transaction ID: {transactionToDelete.transaction_id}</p>
                  <p className="text-sm">Customer: {transactionToDelete.customer_name || transactionToDelete.customer_email}</p>
                  <p className="text-sm">Amount: {transactionToDelete.amount} {transactionToDelete.currency}</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Deleting...' : 'Delete Transaction'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TransactionHistory;